import type { CareOrder } from '@talos/kyoko';

export type SubAccountWarning = (
  | {
      mode: 'create';
      hasDifferingSelectedSubAccounts: boolean;
    }
  | {
      mode: 'modify';
      nettedOrderSubAccount?: string;
    }
) & {
  tooltip?: string;
};

function hasDifferingSubAccounts(careOrders: Pick<CareOrder, 'SubAccount'>[]) {
  const subAccounts = careOrders.flatMap(careOrder => (careOrder.SubAccount == null ? [] : [careOrder.SubAccount]));
  const firstSubAccount = subAccounts.at(0);

  return firstSubAccount != null && subAccounts.some(subAccount => subAccount !== firstSubAccount);
}

/**
 * A new netted order inherits a sub account only when every selected member agrees.
 * An absent common sub account is intentionally omitted from the request.
 */
export function getNewNettedOrderSubAccount(careOrders: Pick<CareOrder, 'SubAccount'>[]) {
  const subAccounts = careOrders.flatMap(careOrder => (careOrder.SubAccount == null ? [] : [careOrder.SubAccount]));

  if (subAccounts.length === 0 || hasDifferingSubAccounts(careOrders)) {
    return undefined;
  }

  return subAccounts.at(0);
}

export function getSubAccountWarning({
  isExistingNettedOrder,
  memberCareOrders,
  nettedCareOrder,
}: {
  /** Whether or not we are opening the preview dialog with an existing netted order */
  isExistingNettedOrder: boolean;
  nettedCareOrder?: Pick<CareOrder, 'SubAccount'>;
  memberCareOrders: Pick<CareOrder, 'SubAccount'>[];
}): SubAccountWarning {
  if (isExistingNettedOrder && nettedCareOrder != null) {
    const nettedOrderSubAccount = nettedCareOrder.SubAccount;
    const hasDifferingSubAccount = memberCareOrders.some(
      careOrder => careOrder.SubAccount != null && careOrder.SubAccount !== nettedOrderSubAccount
    );

    return {
      mode: 'modify',
      nettedOrderSubAccount,
      tooltip: hasDifferingSubAccount
        ? 'Some selected care orders have a different sub account than the netted order. Those sub accounts are ignored.'
        : undefined,
    };
  }

  const hasDifferingSelectedSubAccounts = hasDifferingSubAccounts(memberCareOrders);

  return {
    mode: 'create',
    hasDifferingSelectedSubAccounts,
    tooltip: hasDifferingSelectedSubAccounts
      ? 'The selected care orders have differing sub accounts, so the netted order will not have a sub account.'
      : undefined,
  };
}
